import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import type { OktaAuth } from '@okta/okta-auth-js';
import { QueryClientProvider } from '@tanstack/react-query';
import { env } from 'env';

import store from 'state/store';
import queryClient from 'utils/query-client';
import { getOktaUserId } from 'helpers/okta-session';

import App from 'app';
import * as serviceWorker from 'service-worker';
import * as FullStory from '@fullstory/browser';

import { getCurrentAccount, updateUser } from 'api/generated/users/users';

import powerbi from 'utils/powerbi';
import 'styles/index.css';
import 'react-tooltip/dist/react-tooltip.css';
import oktaAuth from 'utils/okta/use-okta';

// Okta's `OnAuthRequiredFunction` signature expects `void | Promise<void>`.
// The legacy JS code returned a `<Navigate>` element here, but Okta discards
// the return value — the element was constructed and thrown away. Navigation
// to /login is actually handled by `<RequireAuth>` and the route table.
const onAuthRequired = (): void => {};

powerbi.preload({
  type: 'report',
  embedUrl: 'https://app.powerbi.com/reportEmbed',
});

const restoreOriginalUri = async (
  _oktaAuth: OktaAuth,
  originalUri?: string
): Promise<void> => {
  const invalidPaths = ['/implicit', '/login'];

  const isValidRedirect =
    originalUri && !invalidPaths.some((path) => originalUri.includes(path));
  const date = new Date();

  const oktaId = getOktaUserId();

  if (!oktaId) {
    window.location.replace(isValidRedirect ? originalUri : '/');

    return;
  }

  await getCurrentAccount(oktaId).then((userData) => {
    const oktaUserId = userData.oktaUserId;

    if (!oktaUserId) {
      window.location.replace(isValidRedirect ? originalUri : '/');

      return;
    }

    updateUser(oktaUserId, {
      ...userData,
      lastLogin: date.toISOString(),
    }).finally(() => {
      window.location.replace(isValidRedirect ? originalUri : '/');
    });
  });
};

const oktaUserId = getOktaUserId();

const appEnv = String(env.REACT_APP_ENV ?? '').toLowerCase();
const isFullStoryEnabled = appEnv && !['dev', 'qa'].includes(appEnv);

if (isFullStoryEnabled) {
  FullStory.init({ orgId: 'o-1CBCVA-na1' });

  const storedTokens = window.localStorage.getItem('okta-token-storage');
  const oktaStorage = storedTokens ? JSON.parse(storedTokens) : null;
  const email = oktaStorage?.idToken?.claims?.email;

  if (oktaUserId) {
    FullStory.identify(oktaUserId, { email });
  }
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element #root not found in document');
}

const root = ReactDOM.createRoot(rootElement);

root.render(
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Security
          oktaAuth={oktaAuth}
          onAuthRequired={onAuthRequired}
          restoreOriginalUri={restoreOriginalUri}
        >
          <App />
        </Security>
      </BrowserRouter>
    </QueryClientProvider>
  </Provider>
);

serviceWorker.unregister();
